import { createFileRoute, Link } from "@tanstack/react-router";
import { Breadcrumbs, breadcrumbJsonLd } from "@/components/Breadcrumbs";
import { GuiasRelacionadas } from "@/components/GuiasRelacionadas";
import hero1280Asset from "../assets/hero-1280.webp.asset.json";
import hero1920Asset from "../assets/hero-1920.webp.asset.json";
import hero640Asset from "../assets/hero-640.webp.asset.json";

const srcSet = `${hero640Asset.url} 640w, ${hero1280Asset.url} 1280w, ${hero1920Asset.url} 1920w`;
const OG_IMAGE = `https://elpajardebenito.es${hero1280Asset.url}`;
const URL_CANON = "https://elpajardebenito.es/casa-rural-sierra-norte-guadalajara";

const faqs = [
  { q: "¿Dónde está exactamente la casa?", a: "En El Ordial, un pequeño pueblo de la Sierra Norte de Guadalajara, al pie del Pico Alto Rey (1.852 m) y muy cerca de los pueblos negros." },
  { q: "¿Qué rutas de senderismo salen desde el pueblo?", a: "Desde El Ordial parte la ruta al Pico Alto Rey y su ermita. Cerca se enlaza con la Senda de los Pueblos Negros y con rutas al Hayedo de Tejera Negra." },
  { q: "¿Es buena zona para ver estrellas?", a: "Sí. La Sierra Norte de Guadalajara es una de las zonas de menor contaminación lumínica del centro peninsular. Se ve la Vía Láctea a simple vista." },
  { q: "¿Qué pueblos negros hay cerca?", a: "Valverde de los Arroyos, Majaelrayo, Campillo de Ranas, Roblelacasa y Umbralejo, todos a menos de 30 minutos en coche." },
];

export const Route = createFileRoute("/casa-rural-sierra-norte-guadalajara")({
  head: () => ({
    meta: [
      { title: "Casa rural en la Sierra Norte de Guadalajara · El Pajar de Benito" },
      { name: "description", content: "Casa rural en la Sierra Norte de Guadalajara, al pie del Pico Alto Rey. Pueblos negros, Hayedo de Tejera Negra y cielos estrellados. Reserva directa." },
      { property: "og:title", content: "Casa rural en la Sierra Norte de Guadalajara" },
      { property: "og:description", content: "Al pie del Alto Rey y cerca de los pueblos negros y del Hayedo de Tejera Negra." },
      { property: "og:url", content: URL_CANON },
      { property: "og:type", content: "website" },
      { property: "og:image", content: OG_IMAGE },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: OG_IMAGE },
    ],
    links: [{ rel: "canonical", href: URL_CANON }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbJsonLd([
            { label: "Inicio", to: "/" },
            { label: "Sierra Norte de Guadalajara", to: "/casa-rural-sierra-norte-guadalajara" },
          ]),
        ),
      },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <>
      <Breadcrumbs items={[{ label: "Inicio", to: "/" }, { label: "Sierra Norte de Guadalajara" }]} />
      <section className="relative h-[55vh] min-h-[380px] w-full overflow-hidden">
        <img src={hero1280Asset.url} srcSet={srcSet} sizes="100vw" alt="Casa rural en la Sierra Norte de Guadalajara" width={1920} height={1280} className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-black/50" />
        <div className="relative z-10 mx-auto flex h-full max-w-5xl flex-col justify-end px-4 pb-16 md:px-6">
          <p className="text-xs uppercase tracking-[0.28em] text-white/80">El Ordial · Sierra del Alto Rey</p>
          <h1 className="mt-3 font-serif text-4xl text-white md:text-5xl">Casa rural en la Sierra Norte de Guadalajara</h1>
          <p className="mt-4 max-w-2xl text-white/90">Al pie del Pico Alto Rey y a un paso de los pueblos negros y el Hayedo de Tejera Negra.</p>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-4 py-16 md:px-6">
        <h2 className="font-serif text-3xl text-foreground">La sierra que no imaginabas tan cerca</h2>
        <p className="mt-4 text-muted-foreground">Bosques de roble y haya, pueblos de pizarra y cielos limpios. La <strong>Sierra Norte de Guadalajara</strong> es uno de los rincones mejor conservados del centro de España, y <strong>El Pajar de Benito</strong> está justo en su corazón: en El Ordial, al pie del Alto Rey.</p>

        <div className="mt-10 grid gap-6 md:grid-cols-2">
          {[
            { t: "Pico Alto Rey (1.852 m)", d: "Ascensión clásica a la cumbre con su ermita románica y vistas de 360°." },
            { t: "Hayedo de Tejera Negra", d: "Parque Natural y Reserva de la Biosfera. Espectacular en otoño." },
            { t: "Pueblos negros", d: "Valverde, Majaelrayo, Campillo, Roblelacasa: arquitectura de pizarra a menos de 30 min." },
            { t: "Cañón del Río Dulce", d: "Paisaje protegido con buitreras y miradores." },
          ].map((b) => (
            <div key={b.t} className="rounded-sm border border-border bg-card p-6">
              <h3 className="font-serif text-lg">{b.t}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{b.d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-4 py-16 md:px-6">
        <h2 className="font-serif text-3xl text-foreground">Preguntas frecuentes</h2>
        <div className="mt-8 space-y-6">
          {faqs.map((f) => (
            <div key={f.q}>
              <h3 className="font-medium text-foreground">{f.q}</h3>
              <p className="mt-2 text-muted-foreground">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <GuiasRelacionadas slugs={["que-ver-sierra-norte-guadalajara", "subida-alto-rey", "estrellas-alto-rey"]} />

      <section className="mx-auto max-w-3xl px-4 pb-24 text-center md:px-6">
        <h2 className="font-serif text-3xl text-foreground md:text-4xl">Ven a conocer la Sierra Norte</h2>
        <p className="mt-4 text-muted-foreground">Consulta fechas y reserva directa desde nuestra web.</p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Link to="/reserva" className="rounded-full bg-primary px-6 py-3 text-sm font-medium text-primary-foreground hover:bg-primary/90">Ver disponibilidad</Link>
          <Link to="/entorno" className="rounded-full border border-border px-6 py-3 text-sm font-medium hover:bg-muted">Explorar el entorno</Link>
        </div>
      </section>
    </>
  );
}